import { motion, useReducedMotion } from 'motion/react';

import { ColorPicker } from '../../../../../primitives/color-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../primitives/select';
import { LabelWithoutRef } from '../../../../label/label-without-ref';

import { cn } from '../../../../../utils/cn';

import { SquareDashedTopSolidIcon } from '../../../../../svg/square-dashed-top-solid-icon';
import { EMAIL_COLORS } from '../../constants/colors';
import { BorderRadius } from '../controls/border-radius';
import { InputNumberWithSteps } from '../controls/input-number-with-steps';
import type { BorderSettings } from './types';

const BORDER_SIZE_STEPS = [0, 1, 2, 3, 4, 6, 8, 12, 16];
const BORDER_SIDES = [
  ['top', 'Superior', 'rotate-0'],
  ['bottom', 'Inferior', 'rotate-180'],
  ['left', 'Izquierdo', 'rotate-270'],
  ['right', 'Derecho', 'rotate-90'],
] as const;

type AppearanceProps = BorderSettings & {
  complementaryLabel?: string;
  backgroundColor?: string;
  borderRadius?: number | null;
  className?: string;
  onBackgroundColorChange?: (backgroundColor?: string | null) => void;
  onBorderRadiusChange?: (borderRadius?: number | null) => void;
  borderColor?: string;
  onBorderColorChange?: (borderColor: string | null, settings: BorderSettings) => void;
  onBorderStyleChange?: (value: BorderSettings['borderStyle']) => void;
  onBorderWidthsChange?: (value: BorderSettings['borderWidths']) => void;
};

export function Appearance({
  complementaryLabel,
  backgroundColor,
  className,
  onBackgroundColorChange,
  borderColor,
  onBorderColorChange,
  borderRadius,
  onBorderRadiusChange,
  borderStyle,
  borderWidths,
  onBorderStyleChange,
  onBorderWidthsChange,
}: AppearanceProps) {
  const widths = borderWidths ?? { top: 1, right: 1, bottom: 1, left: 1 };
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-xs font-medium">Apariencia {complementaryLabel}</p>
      <div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <div>
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Fondo
            </LabelWithoutRef>
            <ColorPicker
              colors={EMAIL_COLORS}
              isCleanColorVisible
              noneColorLabel="Sin personalizar"
              value={backgroundColor}
              onValueChange={(value) => onBackgroundColorChange?.(value ?? null)}
            />
          </div>
          <BorderRadius value={borderRadius} onChange={onBorderRadiusChange} />
          <div>
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Borde
            </LabelWithoutRef>
            <ColorPicker
              colors={EMAIL_COLORS}
              isCleanColorVisible
              noneColorLabel="Sin personalizar"
              value={borderColor}
              onValueChange={(value) =>
                onBorderColorChange?.(
                  value ?? null,
                  !borderColor && value
                    ? {
                        borderStyle: 'solid',
                        borderWidths: { top: 1, right: 1, bottom: 1, left: 1 },
                      }
                    : {},
                )
              }
            />
          </div>
        </div>
        <motion.div
          className="overflow-hidden"
          initial={false}
          animate={{
            height: borderColor ? 'auto' : 0,
            opacity: borderColor ? 1 : 0,
            marginTop: borderColor ? 12 : 0,
          }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden={!borderColor}
          inert={!borderColor}
        >
          <fieldset className="grid gap-y-2 rounded-3xl bg-muted/50 p-4" disabled={!borderColor}>
            <div>
              <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
                Estilo de borde
              </LabelWithoutRef>
              <Select
                value={borderStyle ?? 'solid'}
                disabled={!borderColor}
                onValueChange={(value) =>
                  onBorderStyleChange?.(value as NonNullable<BorderSettings['borderStyle']>)
                }
              >
                <SelectTrigger size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="solid">Sólido</SelectItem>
                  <SelectItem value="dashed">Discontinuo</SelectItem>
                  <SelectItem value="dotted">Punteado</SelectItem>
                  <SelectItem value="double">Doble</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {BORDER_SIDES.map(([side, label, iconClassName]) => (
                <div key={side} className="min-w-0">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <SquareDashedTopSolidIcon className={iconClassName} />
                    <LabelWithoutRef className="text-xs font-medium">{label}</LabelWithoutRef>
                  </div>
                  <InputNumberWithSteps
                    ariaLabel={label}
                    min={0}
                    step={1}
                    steps={BORDER_SIZE_STEPS}
                    disabled={!borderColor}
                    value={widths[side]}
                    onChange={(value) =>
                      onBorderWidthsChange?.({ ...widths, [side]: Math.max(0, value ?? 0) })
                    }
                  />
                </div>
              ))}
            </div>
          </fieldset>
        </motion.div>
      </div>
    </div>
  );
}
